import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { toastServerError } from "helpers";
import { useDebouncedValue } from "hooks";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { useEffect, useMemo, useRef, useState } from "react";
// Deep path, not the `modules/aiTickets` barrel: that barrel reaches TicketModal,
// which imports back into this module, and the cycle leaves one of them
// undefined at module init.
import { ManualReturnFlow } from "modules/aiTickets/widgets/ManualReturnFlow";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Modal } from "ui";

import {
  useGetConversation,
  useGetConversationMessages,
  useGetConversations,
  useGetTurnDetail,
  useMarkReadConversation,
  useSendMessage,
  useSetConversationMode,
} from "../../apis";
import { ConversationMode } from "../../apis/useSetConversationMode";
import {
  ChatMessage,
  ConversationContact,
  MessageDirection,
  WhatsAppConversation,
  WhatsAppConversationMessagesResponse,
  WhatsAppConversationsListResponse,
  WhatsAppMessage,
} from "../../aiConversations.types";
import {
  ChatView,
  ConversationsList,
  ConversationsStatusFilter,
  CorrectionPanel,
} from "../../components";
import {
  AI_CONVERSATIONS_QUERY_KEY,
  WhatsAppConversationStatus,
  WhatsAppMessageDirection,
  WhatsAppMessageSender,
  WhatsAppMessageStatus,
  WhatsAppMessageType,
} from "../../constants/enums";
import {
  buildOptimisticId,
  chatMessageFromTurn,
  insertOrReplaceMessage,
  modeSwitchState,
  removeMessageById,
  toTurnFields,
  turnVoiceTranscripts,
  withModeOf,
} from "../../helpers";
import {
  AiTypingProvider,
  useAiConversationsRealTimeUpdate,
  useAiTyping,
} from "../../hooks";

import styles from "./AIConversations.module.scss";

type ConversationsInfinite = InfiniteData<WhatsAppConversationsListResponse>;
type MessagesInfinite = InfiniteData<WhatsAppConversationMessagesResponse>;

const formatDate = (iso: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const formatTime = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toConversationContact = (
  conversation: WhatsAppConversation,
): ConversationContact => ({
  id: conversation.id,
  name: conversation.customerDisplayName,
  phone: conversation.customerPhone,
  avatarUrl: conversation.customerAvatarUrl ?? undefined,
  channel: "WhatsApp",
  preview: "",
  date: formatDate(conversation.lastInboundAt ?? conversation.updatedAt),
});

const FALLBACK_LABELS: Record<WhatsAppMessageType, string> = {
  [WhatsAppMessageType.Text]: "",
  [WhatsAppMessageType.Image]: "[image]",
  [WhatsAppMessageType.Voice]: "[voice]",
  [WhatsAppMessageType.Document]: "[document]",
  [WhatsAppMessageType.Video]: "[video]",
  [WhatsAppMessageType.TemplateCarousel]: "[template carousel]",
  [WhatsAppMessageType.ProductCard]: "[product card]",
  [WhatsAppMessageType.PackCard]: "[pack card]",
};

// A single photo: the customer's own, one an agent sent, a product card or a
// pack card. The last two carry their picture in mediaUrl and their caption in
// text, so they render like any other captioned image.
const IMAGE_MESSAGE_TYPES = [
  WhatsAppMessageType.Image,
  WhatsAppMessageType.ProductCard,
  WhatsAppMessageType.PackCard,
];

const toChatMessage = (message: WhatsAppMessage): ChatMessage => {
  const isCustomer = message.sender === WhatsAppMessageSender.Customer;
  const isAi = message.sender === WhatsAppMessageSender.Ai;
  const base = {
    id: message.id,
    direction: (isCustomer ? "incoming" : "outgoing") as MessageDirection,
    isAI: isAi,
    time: formatTime(message.createdAt),
    ...toTurnFields(message),
  };

  // Ahead of the text branch: a carousel and the Flow picker both carry body
  // text, and rendering that text alone is what used to hide their products.
  if (message.cards?.length) {
    return {
      ...base,
      variant: "carousel",
      cards: message.cards,
      text: message.text ?? undefined,
    };
  }

  if (IMAGE_MESSAGE_TYPES.includes(message.messageType) && message.mediaUrl) {
    return {
      ...base,
      variant: "image",
      mediaUrl: message.mediaUrl,
      text: message.text ?? undefined,
    };
  }

  if (message.messageType === WhatsAppMessageType.Video && message.mediaUrl) {
    return {
      ...base,
      variant: "video",
      mediaUrl: message.mediaUrl,
      text: message.text ?? undefined,
    };
  }

  if (message.messageType === WhatsAppMessageType.Voice && message.mediaUrl) {
    return { ...base, variant: "audio", mediaUrl: message.mediaUrl };
  }

  if (message.text) {
    return { ...base, variant: "text", text: message.text };
  }

  return {
    ...base,
    variant: "text",
    text: FALLBACK_LABELS[message.messageType],
  };
};

const toToggleMode = (status: WhatsAppConversationStatus): ConversationMode =>
  status === WhatsAppConversationStatus.Human
    ? WhatsAppConversationStatus.Human
    : WhatsAppConversationStatus.Ai;

const AIConversations = () => (
  <AiTypingProvider>
    <AIConversationsContent />
  </AiTypingProvider>
);

const AIConversationsContent = () => {
  const { t } = useTranslation();
  // Set when an order deep-links here. The thread is very likely NOT in the
  // first page of the list, so it is fetched by id and merged in below.
  const [searchParams] = useSearchParams();
  const deepLinkConversationId = searchParams.get("conversationId");
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(deepLinkConversationId);

  const queryClient = useQueryClient();
  useAiConversationsRealTimeUpdate();
  const { isTyping } = useAiTyping();
  const { user } = useProfile({ enabled: false });
  const currentAdminId = user.id ?? null;
  const isSuperAdmin = user.role === "Super";

  // "Correct this reply": a display gate only, the backend checks the
  // permission again. Set to unavailable when the lessons backend answers 404
  // (not deployed yet), which hides the button for the rest of the visit.
  const canManageAiLessons = Boolean(
    user.role === "Super" ||
    user.permissions?.includes(StaffManagerPermissions.ManageAiLessons),
  );
  const [lessonsUnavailable, setLessonsUnavailable] = useState(false);
  const [correctionTarget, setCorrectionTarget] = useState<ChatMessage | null>(
    null,
  );
  // The conversation the correction was opened from. Switching threads renders
  // once before the reset below runs; this keeps that render from asking for
  // the old reply's turn in the new conversation.
  const [correctionConversationId, setCorrectionConversationId] = useState<
    string | null
  >(null);
  // What the assistant heard in the voice notes of the turns opened in the
  // correction panel, by message id, kept for the open conversation so the
  // voice bubbles can show it. The panel reads the same turn query, so this
  // costs no extra request.
  const [voiceTranscripts, setVoiceTranscripts] = useState<
    Record<string, string>
  >({});

  // The conversation a Super Admin asked to give back to the AI while another
  // admin holds it, waiting for the confirmation. Kept by id so the modal acts
  // on the conversation it was opened from.
  const [giveBackConversationId, setGiveBackConversationId] = useState<
    string | null
  >(null);

  const [searchInput, setSearchInput] = useState("");
  const [isManualReturnOpen, setIsManualReturnOpen] = useState(false);
  const [statusFilter, setStatusFilter] =
    useState<ConversationsStatusFilter>("all");
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  const trimmedSearch = debouncedSearch.trim();

  const {
    conversations: serverConversations,
    fetchNextPage: fetchNextConversationsPage,
    hasNextPage: hasMoreConversations,
    isFetchingNextPage: isLoadingMoreConversations,
    isLoading: isLoadingConversations,
  } = useGetConversations({
    search: trimmedSearch || undefined,
    status: statusFilter === "all" ? undefined : statusFilter,
  });

  const {
    conversation: deepLinkConversation,
    isLoading: isLoadingDeepLinkConversation,
  } = useGetConversation(deepLinkConversationId);

  // True only while a deep-linked thread is still in flight — `isLoading` also
  // reads true for the disabled query, hence the id check.
  const isDeepLinkPending =
    Boolean(deepLinkConversationId) && isLoadingDeepLinkConversation;

  // The deep-linked thread has to be part of the list, not just selected: the
  // contacts column and the selected-conversation lookup both derive from it.
  const conversations = useMemo(() => {
    if (
      !deepLinkConversation ||
      serverConversations.some((c) => c.id === deepLinkConversation.id)
    ) {
      return serverConversations;
    }

    return [deepLinkConversation, ...serverConversations];
  }, [serverConversations, deepLinkConversation]);

  useEffect(() => {
    // Falling back to the first row while the deep link is still loading would
    // overwrite the requested id, and nothing would bring it back.
    if (isDeepLinkPending) return;
    if (!conversations || conversations.length === 0) return;
    if (
      !selectedConversationId ||
      !conversations.some((c) => c.id === selectedConversationId)
    ) {
      setSelectedConversationId(conversations[0].id);
    }
  }, [conversations, selectedConversationId, isDeepLinkPending]);

  const conversationContacts = useMemo(
    () => (conversations ?? []).map(toConversationContact),
    [conversations],
  );

  const selectedConversation = useMemo(
    () => conversations?.find((c) => c.id === selectedConversationId),
    [conversations, selectedConversationId],
  );

  const selectedContact = useMemo(
    () =>
      selectedConversation ? toConversationContact(selectedConversation) : null,
    [selectedConversation],
  );

  const {
    messages: serverMessages,
    fetchNextPage: fetchOlderMessages,
    hasNextPage: hasOlderMessages,
    isFetchingNextPage: isLoadingOlderMessages,
    isLoading: isLoadingMessages,
  } = useGetConversationMessages(
    { conversationId: selectedConversationId ?? "" },
    { enabled: Boolean(selectedConversationId) },
  );

  const chatMessages = useMemo(
    () => (serverMessages ?? []).map(toChatMessage),
    [serverMessages],
  );

  const markReadMutation = useMarkReadConversation({
    onSuccess: (updated) => {
      queryClient.setQueriesData<ConversationsInfinite>(
        { queryKey: [AI_CONVERSATIONS_QUERY_KEY, "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: page.data.map((c) =>
                c.id === updated.id ? { ...c, unreadCount: 0 } : c,
              ),
            })),
          };
        },
      );
    },
  });

  useEffect(() => {
    if (!selectedConversationId) return;
    markReadMutation.mutate(selectedConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId]);

  // A correction belongs to the conversation it was opened from.
  useEffect(() => {
    setCorrectionTarget(null);
    setVoiceTranscripts({});
  }, [selectedConversationId]);

  const activeCorrection =
    correctionTarget && correctionConversationId === selectedConversationId
      ? correctionTarget
      : null;

  const openCorrection = (message: ChatMessage) => {
    setCorrectionTarget(message);
    setCorrectionConversationId(selectedConversationId);
  };

  const { turn: correctionTurn } = useGetTurnDetail(
    activeCorrection ? selectedConversationId : null,
    activeCorrection?.turnMessageId ?? null,
  );

  useEffect(() => {
    if (!correctionTurn) return;
    const found = turnVoiceTranscripts(correctionTurn);
    if (Object.keys(found).length === 0) return;
    setVoiceTranscripts((current) => ({ ...current, ...found }));
  }, [correctionTurn]);

  const handleLessonsUnavailable = () => {
    setLessonsUnavailable(true);
    setCorrectionTarget(null);
  };

  // "Create a lesson" on the "To review" page links here with the reply's
  // turn (`&turnMessageId=`): the correction panel opens on that reply once
  // the turn is loaded. It is the query the panel reads, so the panel costs no
  // second request. It happens once per link: closing the panel does not
  // reopen it.
  const deepLinkTurnMessageId = searchParams.get("turnMessageId");
  const shouldOpenDeepLinkTurn =
    Boolean(deepLinkConversationId && deepLinkTurnMessageId) &&
    selectedConversationId === deepLinkConversationId &&
    canManageAiLessons &&
    !lessonsUnavailable;
  const {
    turn: deepLinkTurn,
    error: deepLinkTurnError,
    isUnavailable: isDeepLinkTurnUnavailable,
  } = useGetTurnDetail(
    shouldOpenDeepLinkTurn ? deepLinkConversationId : null,
    shouldOpenDeepLinkTurn ? deepLinkTurnMessageId : null,
  );
  const openedDeepLinkTurnRef = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldOpenDeepLinkTurn) return;
    const linkKey = `${deepLinkConversationId}/${deepLinkTurnMessageId}`;
    if (openedDeepLinkTurnRef.current === linkKey) return;
    // A backend without the turn route: hidden like the panel does.
    if (isDeepLinkTurnUnavailable) {
      handleLessonsUnavailable();
      return;
    }
    if (deepLinkTurnError) {
      openedDeepLinkTurnRef.current = linkKey;
      toastServerError(deepLinkTurnError);
      return;
    }
    if (!deepLinkTurn) return;

    openedDeepLinkTurnRef.current = linkKey;
    const message = chatMessageFromTurn(deepLinkTurn, formatTime);
    if (!message) {
      // The conversation stays open.
      toast(t("aiReview.noCorrectableReply"));
      return;
    }
    setCorrectionTarget(message);
    setCorrectionConversationId(deepLinkConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldOpenDeepLinkTurn,
    deepLinkTurn,
    deepLinkTurnError,
    isDeepLinkTurnUnavailable,
  ]);

  const sendMessageMutation = useSendMessage({
    onMutate: ({ conversationId, text, imageUrl, audioUrl }) => {
      const optimisticId = buildOptimisticId(conversationId);
      const optimistic: WhatsAppMessage = {
        id: optimisticId,
        conversationId,
        direction: WhatsAppMessageDirection.Outbound,
        sender: WhatsAppMessageSender.Admin,
        senderAdminId: null,
        messageType: audioUrl
          ? WhatsAppMessageType.Voice
          : imageUrl
            ? WhatsAppMessageType.Image
            : WhatsAppMessageType.Text,
        text: text ?? null,
        mediaUrl: audioUrl ?? imageUrl ?? null,
        cards: null,
        status: WhatsAppMessageStatus.Sent,
        inResponseToMessageId: null,
        responseSequence: 0,
        origin: null,
        createdAt: new Date().toISOString(),
      };
      queryClient.setQueriesData<MessagesInfinite>(
        {
          queryKey: [AI_CONVERSATIONS_QUERY_KEY, "messages", conversationId],
        },
        (current) => insertOrReplaceMessage(current, optimistic),
      );
      return { optimisticId };
    },
    onSuccess: (message) => {
      queryClient.setQueriesData<MessagesInfinite>(
        {
          queryKey: [
            AI_CONVERSATIONS_QUERY_KEY,
            "messages",
            message.conversationId,
          ],
        },
        (current) => insertOrReplaceMessage(current, message),
      );
    },
    onError: (_err, _vars, context) => {
      const optimisticId = (context as { optimisticId?: string } | undefined)
        ?.optimisticId;
      if (!optimisticId || !selectedConversationId) return;
      queryClient.setQueriesData<MessagesInfinite>(
        {
          queryKey: [
            AI_CONVERSATIONS_QUERY_KEY,
            "messages",
            selectedConversationId,
          ],
        },
        (current) => removeMessageById(current, optimisticId),
      );
    },
  });

  const setModeMutation = useSetConversationMode({
    onSuccess: (updated) => {
      queryClient.setQueriesData<ConversationsInfinite>(
        { queryKey: [AI_CONVERSATIONS_QUERY_KEY, "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: page.data.map((c) => withModeOf(c, updated)),
            })),
          };
        },
      );
      // A deep-linked thread outside the loaded page shows its own query's
      // copy (withModeOf).
      queryClient.setQueryData<WhatsAppConversation>(
        [AI_CONVERSATIONS_QUERY_KEY, "one", updated.id],
        (current) => current && withModeOf(current, updated),
      );
    },
  });

  const handleSend = (payload: {
    text?: string;
    imageUrl?: string;
    audioUrl?: string;
  }) => {
    if (!selectedConversationId) return;
    sendMessageMutation.mutate({
      conversationId: selectedConversationId,
      ...payload,
    });
  };

  // A conversation another admin holds: locked, except for a Super Admin, who
  // may give it back to the AI after a confirmation (modeSwitchState).
  const { ownedByOtherAdmin, switchLocked, confirmBeforeAi } = modeSwitchState(
    selectedConversation,
    currentAdminId,
    isSuperAdmin,
  );

  // Another thread, or the other admin let it go: a pending confirmation is
  // dropped, so it never opens again by itself later.
  useEffect(() => {
    setGiveBackConversationId(null);
  }, [selectedConversationId, confirmBeforeAi]);

  const handleModeChange = (next: ConversationMode) => {
    if (!selectedConversationId) return;
    if (confirmBeforeAi && next === WhatsAppConversationStatus.Ai) {
      setGiveBackConversationId(selectedConversationId);
      return;
    }
    setModeMutation.mutate({
      conversationId: selectedConversationId,
      mode: next,
    });
  };

  // On an error the hook's toast shows and the modal stays open, to retry or
  // cancel.
  const confirmGiveBack = () => {
    if (!giveBackConversationId) return;
    setModeMutation.mutate(
      {
        conversationId: giveBackConversationId,
        mode: WhatsAppConversationStatus.Ai,
      },
      { onSuccess: () => setGiveBackConversationId(null) },
    );
  };

  // Once sent, the request cannot be called back: Cancel, × and Esc closing
  // the modal then would read as cancelled while the conversation still goes
  // back to the AI (review, 4 Nov 2026). They wait for the answer.
  const closeGiveBack = () => {
    if (setModeMutation.isLoading) return;
    setGiveBackConversationId(null);
  };

  const composerDisabled = selectedConversation
    ? selectedConversation.status !== WhatsAppConversationStatus.Human ||
      ownedByOtherAdmin
    : true;

  const composerDisabledReason = ownedByOtherAdmin
    ? t("Another admin has taken over this conversation.")
    : t(
        "AI is handling this conversation. Turn it off to send a message manually.",
      );

  return (
    <div className={styles.workspace}>
      <div className={styles.card}>
        <aside className={styles.sidebar}>
          <ConversationsList
            conversations={conversationContacts}
            selectedId={selectedConversationId}
            onSelect={(id) => setSelectedConversationId(id)}
            onLoadMore={() => fetchNextConversationsPage()}
            hasMore={hasMoreConversations}
            isLoadingMore={isLoadingMoreConversations}
            isLoading={isLoadingConversations}
            search={searchInput}
            onSearchChange={setSearchInput}
            searchPlaceholder="Search by name or phone..."
            status={statusFilter}
            onStatusChange={setStatusFilter}
          />
        </aside>

        <main className={styles.main}>
          {selectedContact && selectedConversation ? (
            <ChatView
              contact={selectedContact}
              messages={chatMessages}
              mode={toToggleMode(selectedConversation.status)}
              onModeChange={handleModeChange}
              toggleDisabled={switchLocked}
              togglingMode={setModeMutation.isLoading}
              onSend={handleSend}
              sending={sendMessageMutation.isLoading}
              composerDisabled={composerDisabled}
              composerDisabledReason={composerDisabledReason}
              onLoadOlder={() => fetchOlderMessages()}
              hasOlder={hasOlderMessages}
              isLoadingOlder={isLoadingOlderMessages}
              isLoadingMessages={isLoadingMessages}
              aiTyping={isTyping(selectedConversation.id)}
              onCreateReturn={() => setIsManualReturnOpen(true)}
              canCorrect={canManageAiLessons && !lessonsUnavailable}
              onCorrectMessage={openCorrection}
              voiceTranscripts={voiceTranscripts}
            />
          ) : null}
        </main>

        {activeCorrection && selectedConversation ? (
          <aside className={styles.correction}>
            <CorrectionPanel
              key={activeCorrection.id}
              conversationId={selectedConversation.id}
              message={activeCorrection}
              onClose={() => setCorrectionTarget(null)}
              onUnavailable={handleLessonsUnavailable}
            />
          </aside>
        ) : null}
      </div>

      {selectedConversation && (
        <ManualReturnFlow
          conversationId={selectedConversation.id}
          isOpen={isManualReturnOpen}
          onClose={() => setIsManualReturnOpen(false)}
        />
      )}

      {/* The question is the example the « go » was given on (4 Nov 2026),
          used as is; the title and the button are proposed 4 Nov 2026. */}
      <Modal
        isOpen={
          giveBackConversationId !== null &&
          giveBackConversationId === selectedConversationId &&
          confirmBeforeAi
        }
        title="Give this conversation back to the AI"
        okText="Give back to the AI"
        cancelText="Cancel"
        isLoading={setModeMutation.isLoading}
        onOk={confirmGiveBack}
        onClose={closeGiveBack}
      >
        <p className={styles.confirmText}>
          {t(
            "This conversation is being handled by another admin. Give it back to the AI?",
          )}
        </p>
      </Modal>
    </div>
  );
};

export default AIConversations;
